import {
    DateField,
    ReferenceField,
    Show,
    TextField
} from '@/components/admin';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, DollarSign, FileText } from 'lucide-react';
import { useRecordContext } from 'ra-core';
import { Link } from 'react-router-dom';
import { ThreeWayMatchBadge } from '../documents/invoices/ThreeWayMatchBadge';
import { StatusBadge } from '../documents/shared/StatusBadge';
import type { VendorBill } from '../types';
import { VendorBillActions } from './VendorBillActions';
const ShowTitle = () => {
    const record = useRecordContext();
    if (!record) return null;
    return (
        <div className="flex items-center gap-3">
            <Button asChild variant="outline" size="sm" className="gap-1.5 bg-primary/10 hover:bg-primary/20 text-primary border-primary/20 font-semibold cursor-pointer h-9 px-4 rounded-lg">
                <Link to="/vendor_bills" className="flex items-center gap-1.5">
                    <ArrowLeft className="w-4 h-4" /> Back to Vendor Bills
                </Link>
            </Button>
            <span className="text-xl font-bold tracking-tight text-slate-900 ml-1">{record.reference}</span>
        </div>
    );
};

const VendorBillShowContent = () => {
    const record = useRecordContext<VendorBill>();
    if (!record) return null;

    return (
        <div className="flex flex-col gap-6 w-full max-w-4xl mt-4">
            <div className="flex items-center gap-4 border-b pb-4 flex-wrap">
                <StatusBadge status={record.status} />
                <span className="text-slate-300">|</span>
                <ThreeWayMatchBadge resource="vendor_bills" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                    <CardHeader className="pb-3 border-b border-slate-100">
                        <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                            <FileText className="w-4 h-4" /> Bill Information
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4 space-y-4">
                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Vendor</span>
                            <span className="font-semibold text-slate-900">
                                <ReferenceField source="vendor_id" reference="vendors" link="show">
                                    <TextField source="name" className="text-blue-600 hover:underline" />
                                </ReferenceField>
                            </span>
                        </div>

                        {record.contact_person && (
                            <div className="flex flex-col gap-1">
                                <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Contact Person</span>
                                <span className="text-sm text-slate-700">{record.contact_person}</span>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1">
                                <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Bill Date</span>
                                <span className="font-medium text-slate-900"><DateField source="bill_date" /></span>
                            </div>
                            <div className="flex flex-col gap-1">
                                <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Due Date</span>
                                <span className="font-medium text-slate-900"><DateField source="due_date" /></span>
                            </div>
                        </div>

                        {(record.po_id || record.grn_id) && (
                            <div className="grid grid-cols-2 gap-4 pt-3 border-t border-slate-100">
                                {record.po_id && (
                                    <div className="flex flex-col gap-1">
                                        <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Linked PO</span>
                                        <ReferenceField source="po_id" reference="purchase_orders" link="show">
                                            <TextField source="reference" className="text-slate-700 font-mono text-sm" />
                                        </ReferenceField>
                                    </div>
                                )}
                                {record.grn_id && (
                                    <div className="flex flex-col gap-1">
                                        <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Linked GRN</span>
                                        <ReferenceField source="grn_id" reference="goods_receipts" link="show">
                                            <TextField source="reference" className="text-slate-700 font-mono text-sm" />
                                        </ReferenceField>
                                    </div>
                                )}
                            </div>
                        )}
                        
                        {record.notes && (
                            <div className="flex flex-col gap-1 pt-3 border-t border-slate-100">
                                <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Notes</span>
                                <span className="text-sm text-slate-700 whitespace-pre-wrap">{record.notes}</span>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="pb-3 border-b border-slate-100">
                        <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                            <DollarSign className="w-4 h-4" /> Amounts
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4 space-y-4">
                        <div className="flex items-center justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500 font-medium">Items Total</span>
                            <span className="font-medium">{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(record.items_total || 0)}</span>
                        </div>
                        <div className="flex items-center justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500 font-medium">Tax Amount</span>
                            <span className="font-medium">{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(record.tax_amount || 0)}</span>
                        </div>
                        <div className="flex items-center justify-between py-2 font-bold text-lg text-slate-900 bg-slate-50 rounded-lg px-3 mt-2">
                            <span>Total Amount</span>
                            <span className="text-rose-700">{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(record.total_amount || 0)}</span>
                        </div>
                        <div className="flex items-center justify-between py-2 font-bold text-lg text-slate-900 bg-emerald-50 rounded-lg px-3">
                            <span>Amount Paid</span>
                            <span className="text-emerald-700">{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(record.amount_paid || 0)}</span>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export const VendorBillShow = () => (
    <Show title={<ShowTitle />} actions={<VendorBillActions />}>
        <VendorBillShowContent />
    </Show>
);
